import React from 'react';

import { useIntl } from 'react-intl';

import { Button, Modal, notification } from 'antd';

import ComponentBlocker from '@src/components/ComponentBlocker';
import { ALLOWED_ROLES } from '@src/packages/utils';
import { WORK_JOURNEY_STATUS } from '@src/packages/workJourney/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import { useWorkJourneyValidationTranslations } from '@src/packages/workJourney/hooks/useWorkJourneyValidationTranslations';
import { notifyWorkJourneySaveError } from '@src/packages/workJourney/utils/submitWorkJourneyForm';
import WorkJourneyService from '@src/services/WorkJourneyService';
import safeExecute from '@src/util/safeExecute';

import styles from './WorkJourneyEditConfirm.module.less';

const workJourneyService = new WorkJourneyService();

const WorkJourneyEditConfirm = () => {
  const {
    workJourneyForm,
    setWorkJourneyForm,
    setIsSaving,
    isSaving,
    orgId,
    modalVisibility,
    toggleModalVisibility,
    setInitialFormState,
  } = useWorkJourneyContext();

  const intl = useIntl();
  const validationTranslations = useWorkJourneyValidationTranslations();

  const handleCancel = () => {
    toggleModalVisibility('updateModal');
  };

  const updateWorkJourney = async (applyChanges = false) => {
    if (isSaving) return;

    setIsSaving(true);
    safeExecute(async () => {
      const data = {
        ...workJourneyForm,
        state: applyChanges ? WORK_JOURNEY_STATUS.PENDING : WORK_JOURNEY_STATUS.UPDATED,
      };

      const result = await workJourneyService
        .update(data, orgId, validationTranslations)
        .catch((error: unknown) => {
          notifyWorkJourneySaveError(error, intl, {
            members: data.members,
            matchUrl: `/${orgId}/-`,
          });
          return undefined;
        })
        .finally(() => {
          setIsSaving(false);
          toggleModalVisibility('updateModal');
        });

      if (result) {
        const message = intl.formatMessage({ id: 'workJourney.updatedSuccessfully' });
        notification.success({ message });
        setWorkJourneyForm(data);
        setInitialFormState(data);
      }
    });
  };
  return (
    <Modal
      className={styles.modal}
      open={modalVisibility.updateModal}
      onCancel={handleCancel}
      closable={false}
      title={null}
      width={520}
      footer={[
        <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}>
          <Button key="cancel" onClick={handleCancel} disabled={isSaving}>
            {intl.formatMessage({ id: 'workJourney.edit.cancel' })}
          </Button>
        </ComponentBlocker>,
        <ComponentBlocker allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}>
          <Button
            key="save"
            type="primary"
            disabled={isSaving}
            onClick={() => updateWorkJourney()}
            loading={isSaving}
          >
            {intl.formatMessage({ id: 'workJourney.edit.save' })}
          </Button>
        </ComponentBlocker>,
      ]}
    >
      <div className={styles.iconContainer}>
        <span className={`material-icons-outlined ${styles.icon}`}>info</span>
      </div>

      <div className={styles.title}>{intl.formatMessage({ id: 'workJourney.edit.saveTitle' })}</div>

      <div className={styles.description}>
        {intl.formatMessage({ id: 'workJourney.edit.saveDescription' })}
      </div>
    </Modal>
  );
};

export default WorkJourneyEditConfirm;
